import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Col,
  Dropdown,
  Form,
  FormProps,
  Input,
  notification,
  Row,
  Select,
  Space
} from 'antd';
import {
  CalendarOutlined,
  CloseCircleFilled,
  DollarCircleOutlined,
  EditFilled,
  EllipsisOutlined,
  IdcardOutlined,
  PlusCircleOutlined,
  SwapRightOutlined,
  UserOutlined
} from '@ant-design/icons';

import { useGetAllLabels } from 'hooks/useGetAllLabels';
import { BookingAction } from 'components/modal/components/booking-action';
import useModalStore from 'stores/useModal';
import { useGetCompanies } from 'hooks/useGetCompanies';
import { useGetSources } from 'hooks/useGetSources';
import useBookingStore from 'stores/useBooking';
import { buildClassName, formatCurrency, toLocalTime, trimTruthyValues } from 'utils';
import {
  BookingLine,
  BookingStatus,
  KinSimStatus,
  GetServiceType,
  ParamsExtraInfo
} from 'services/api/type/booking.type';
import { Labels } from 'services/api/type/labels.type';

import { MENU_ASSIGN_ROOM_VALUE, MenuAssignRoom } from 'constants/common';
import { MAP_ICON_ROOM_STATUS } from 'constants/icon';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import API from 'services/api';
import { refreshBookingList, refreshRoomMap } from 'hooks';
import { ReactComponent as IconBreakfast } from 'assets/images/breakfast.svg';
import { useGetTravelAgencies } from 'hooks/useGetTravelAgencies';
import useCommonStore from 'stores/useCommon';
import { CustomMenuItem, PermissionActionTypes } from 'helper/type/action.type';
import { useGetCampaigns } from 'hooks/useGetCampaigns';
import IBButton from 'helper/permission.action';

interface RoomInfoTabProps {
  bookingLine: BookingLine;
  t: (key: string) => string;
}

const RoomInfoTab = ({ bookingLine, t }: RoomInfoTabProps) => {
  const [form] = Form.useForm();
  const location = useLocation();
  const {
    setIsOpenAssignRoom,
    setIsOpenChangeRoomType,
    setIsOpenCancelAssignRoom,
    setIsOpenCancelRoom,
    setIsOpenCustomer,
    setIsOpenAddService,
    setIsOpenChangeDate,
    setIsOpenChangePrice,
    setInfoConfirmModal,
    setConfirmLoading,
    setIsOpenSetBreakfast,
    setIsOpenUndoCheckIn,
    setIsOpenUndoCheckOut,
    setIsOpenUndoCancelledRoom,
    isOpenBookingDetail,
    setIsOpenIssueKinSim,
    setIsOpenViewKinSimDetails
  } = useModalStore();
  const { currentBranch } = useCommonStore();

  const { setSelectedCustomerId, setBookingLineId } = useBookingStore();

  const { data: companies } = useGetCompanies();
  const { data: sources } = useGetSources();
  const { data: campaigns } = useGetCampaigns();

  const [showInput, setShowInput] = useState<boolean>(false);

  const handleSourceChange = (value: number) => {
    const selectedSourceObj = sources?.find(source => source.id === value);
    if (
      selectedSourceObj?.name.toLowerCase().includes('ota') &&
      !bookingLine.cms_booking_id &&
      !bookingLine.cms_ota_id
    ) {
      setShowInput(true);
    } else {
      setShowInput(false);
    }
  };

  const handleEditOtaId = () => {
    setShowInput(true);
  };

  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [selectedTravelAgency, setSelectedTravelAgency] = useState<any>({});
  const { data: labels } = useGetAllLabels(selectedLabel);
  const { data: travelAgencies } = useGetTravelAgencies(selectedTravelAgency);

  const { mutateAsync: mutateCreateAndAssignLabels } = useMutation({
    mutationFn: ({ bookingLineId, label_name }: { bookingLineId: number; label_name: string }) =>
      API.labels.createAndAssignLabels(bookingLineId, label_name)
  });
  const { mutateAsync: mutateUnassignLabels } = useMutation({
    mutationFn: ({ bookingLineId, label_name }: { bookingLineId: number; label_name: string }) =>
      API.labels.unassignLabels(bookingLineId, label_name)
  });

  const { mutateAsync: mutateUpdateExtraInfo, isPending } = useMutation({
    mutationFn: ({ bookingLineId, params }: { bookingLineId: number; params: ParamsExtraInfo }) =>
      API.booking.updateExtraInfo(bookingLineId, params)
  });
  const { mutateAsync: mutateRemoveService } = useMutation({
    mutationFn: ({
      bookingLineId,
      params
    }: {
      bookingLineId: number;
      params: {
        service_id: number;
      };
    }) => API.booking.removeService(bookingLineId, params)
  });

  useEffect(() => {
    if (isOpenBookingDetail) {
      form.resetFields();
      form.setFieldsValue(
        trimTruthyValues({
          note: bookingLine?.note,
          source_id: bookingLine?.source_id,
          medium_id: bookingLine?.medium_id,
          campaign_id: bookingLine?.campaign_id,
          hotel_travel_agency_id: bookingLine?.hotel_travel_agency_id
        })
      );
    } else {
      setShowInput(false);
      setSelectedTravelAgency({});
    }
    if (sources && bookingLine.source_id) {
      const selectedSourceObj = sources?.find(source => source.id === bookingLine.source_id);
      if (
        selectedSourceObj?.name.toLowerCase().includes('ota') &&
        !bookingLine.cms_booking_id &&
        !bookingLine.cms_ota_id
      ) {
        setShowInput(true);
      } else {
        setShowInput(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingLine.booking_line_id,
    isOpenBookingDetail,
    setShowInput,
    sources,
    bookingLine.source_id,
    bookingLine.cms_booking_id
  ]);

  const handleClickMenu = (menu: any) => {
    switch (menu.key) {
      case MENU_ASSIGN_ROOM_VALUE.CHANGE_ROOM_TYPE:
        setIsOpenChangeRoomType(true);
        break;
      case MENU_ASSIGN_ROOM_VALUE.ASSIGN_ROOM:
        setIsOpenAssignRoom(true);
        break;
      case MENU_ASSIGN_ROOM_VALUE.CANCEL_ASSIGN_ROOM:
        setBookingLineId(Number(bookingLine.booking_line_id));
        setIsOpenCancelAssignRoom(true);
        break;
      case MENU_ASSIGN_ROOM_VALUE.CANCEL_ROOM:
        setBookingLineId(Number(bookingLine.booking_line_id));
        setIsOpenCancelRoom(true);
        break;
      case MENU_ASSIGN_ROOM_VALUE.UNDO_CHECK_IN:
        setBookingLineId(Number(bookingLine.booking_line_id));
        setIsOpenUndoCheckIn(true);
        break;
      case MENU_ASSIGN_ROOM_VALUE.UNDO_CHECK_OUT:
        setBookingLineId(Number(bookingLine.booking_line_id));
        setIsOpenUndoCheckOut(true);
        break;
      case MENU_ASSIGN_ROOM_VALUE.UNDO_CANCELLED_BOOKING:
        setIsOpenUndoCancelledRoom(true);
        break;
      default:
        break;
    }
  };

  const handleShowCustomerModal = () => {
    setIsOpenCustomer(true);
  };

  const handleShowAddService = () => {
    setBookingLineId(bookingLine.booking_line_id);
    setIsOpenAddService(true);
  };

  const handleShowChangeDateModal = () => {
    setBookingLineId(bookingLine.booking_line_id);
    setIsOpenChangeDate(true);
  };

  const handleShowChangePriceModal = () => {
    setIsOpenChangePrice(true);
  };

  // eslint-disable-next-line
  const handleSearch = useCallback(
    _.debounce(async search => {
      setSelectedLabel(search);
    }, 500),
    []
  );
  const onLabelChange = (value: string) => {
    setSelectedLabel(value);
  };

  // eslint-disable-next-line
  const handleSearchTravelAgency = useCallback(
    _.debounce(async value => {
      setSelectedTravelAgency({ name: value });
    }, 500),
    []
  );
  const handleTravelAgencyChange = (value: number, record: any) => {
    if (!record) {
      form.setFieldsValue({
        hotel_travel_agency_id: undefined
      });
      return;
    }
    form.setFieldsValue({
      hotel_travel_agency_id: value,
      source_id: record.source_id || undefined,
      medium_id: record.medium_id || undefined,
      campaign_id: record.campaign_id || undefined
    });
  };

  const handleRemoveService = async (service_id: number) => {
    setInfoConfirmModal(true, {
      title: t('common.confirm.deleteConfirmation'),
      okText: t('common.actions.confirm'),
      onOk: async () => {
        if (bookingLine?.booking_id) {
          try {
            setConfirmLoading(true);
            await mutateRemoveService({
              bookingLineId: bookingLine.booking_line_id,
              params: {
                service_id
              }
            });

            queryClient.invalidateQueries({
              queryKey: [
                QUERY_KEYS.KEY_ROOM,
                QUERY_KEYS.GET_BOOKING_DETAIL,
                bookingLine.booking_line_id
              ]
            });
            setInfoConfirmModal(false);
            notification.success({
              message: t('common.message.serviceDeletedSuccessfully')
            });
          } catch (err: any) {
            notification.error({
              message: t('common.message.failedToDeleteService')
            });
          } finally {
            setConfirmLoading(false);
          }
        }
      }
    });
  };

  const handleSetBreakfast = () => {
    setIsOpenSetBreakfast(true);
  };

  const handleUnassignLabel = async (value: string) => {
    await mutateUnassignLabels({
      bookingLineId: bookingLine.booking_line_id,
      label_name: value
    });
  };

  const onFinish: FormProps['onFinish'] = async (values: ParamsExtraInfo) => {
    try {
      for (const label_name of values?.label_name || '') {
        await mutateCreateAndAssignLabels({
          bookingLineId: bookingLine.booking_line_id,
          label_name: label_name
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_LABELS]
        });
      }
      await mutateUpdateExtraInfo({
        bookingLineId: bookingLine.booking_line_id,
        params: {
          note: values.note,
          hotel_travel_agency_id: values.hotel_travel_agency_id,
          medium_id: values.medium_id,
          source_id: values.source_id,
          campaign_id: values.campaign_id,
          cms_ota_id: values.cms_ota_id
        }
      });
      setShowInput(false);
      setSelectedTravelAgency({});
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, bookingLine.booking_line_id]
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_TRACKING_LOGS]
      });

      refreshBookingList(location);
      refreshRoomMap(location);

      notification.success({
        message: t('common.message.saveSuccessfully')
      });
    } catch (err: any) {
      notification.error({
        message: err.error || t('common.error')
      });
    }
  };

  const menuAssignRoom = useMemo(() => {
    let filteredMenu: CustomMenuItem[] = [];

    if (bookingLine.status === BookingStatus.CONFIRM && !bookingLine.room_id) {
      filteredMenu = MenuAssignRoom.filter(
        item =>
          item.key === MENU_ASSIGN_ROOM_VALUE.CHANGE_ROOM_TYPE ||
          item.key === MENU_ASSIGN_ROOM_VALUE.ASSIGN_ROOM ||
          item.key === MENU_ASSIGN_ROOM_VALUE.CANCEL_ROOM
      );
    }
    if (bookingLine.status === BookingStatus.CONFIRM && bookingLine.room_id) {
      filteredMenu = MenuAssignRoom.filter(
        item =>
          item.key === MENU_ASSIGN_ROOM_VALUE.CANCEL_ASSIGN_ROOM ||
          item.key === MENU_ASSIGN_ROOM_VALUE.CANCEL_ROOM
      );
    }
    if (bookingLine.status === BookingStatus.CHECK_IN) {
      filteredMenu = MenuAssignRoom.filter(
        item => item.key === MENU_ASSIGN_ROOM_VALUE.UNDO_CHECK_IN
      );
    }
    if (bookingLine.status === BookingStatus.CHECK_OUT) {
      filteredMenu = MenuAssignRoom.filter(
        item => item.key === MENU_ASSIGN_ROOM_VALUE.UNDO_CHECK_OUT
      );
    }
    if (bookingLine.status === BookingStatus.CANCEL) {
      filteredMenu = MenuAssignRoom.filter(
        item => item.key === MENU_ASSIGN_ROOM_VALUE.UNDO_CANCELLED_BOOKING
      );
    }

    return filteredMenu.map(item => ({
      ...item,
      label: typeof item.label === 'string' ? t(item.label) : item.label
    }));
  }, [bookingLine, t]);

  const totalMoney = useMemo(() => {
    if (!bookingLine.services?.length) {
      return 0;
    }
    return bookingLine.services.reduce((cur: number, next: GetServiceType) => {
      if (next.is_deleted) {
        return cur;
      }
      const total = next.price * (next?.qty || 0);
      return cur + total;
    }, 0);
  }, [bookingLine.services]);

  const displayBreakfast = useMemo(() => {
    if (!_.isEmpty(bookingLine.breakfast_dates)) {
      const startDate = _.get(bookingLine, 'breakfast_dates.0.date', '');
      const endDate = _.get(
        bookingLine,
        `breakfast_dates.${bookingLine.breakfast_dates.length - 1}.date`,
        ''
      );
      return `${toLocalTime(startDate, 'DD/MM')} - ${toLocalTime(endDate, 'DD/MM')}`;
    }
    return '';
  }, [bookingLine]);

  const isCancelOrCheckOut = [BookingStatus.CHECK_OUT, BookingStatus.CANCEL].includes(
    bookingLine.status
  );

  const IconStatus = MAP_ICON_ROOM_STATUS[bookingLine.status];

  return (
    <Row className="modal-content">
      <Col span={24} md={8}>
        <div className="customer-highlight">
          <UserOutlined />
          <span className="booking-id">
            #{bookingLine.booking_id} - {bookingLine.booking_line_id}
          </span>
          <span className="remain">
            {t('common.bookingData.remaining')}{' '}
            {isCancelOrCheckOut
              ? 0
              : currentBranch?.is_enable_v2
                ? formatCurrency(bookingLine.remain_amount)
                : formatCurrency(bookingLine.total_price - (bookingLine.paid_amount || 0))}
          </span>
        </div>
        <div className="customer-room">
          <div className="flex items-center">
            {IconStatus && (
              <IconStatus
                className={classNames('icon-status', {
                  'icon-status--confirm': bookingLine.status === BookingStatus.CONFIRM,
                  'icon-status--checkin': bookingLine.status === BookingStatus.CHECK_IN,
                  'icon-status--checkout': bookingLine.status === BookingStatus.CHECK_OUT
                })}
              />
            )}
            <span className="room-id">
              {bookingLine.original_room_type_name !== bookingLine.room_type_name && (
                <span className="original-room-type">
                  {bookingLine.original_room_type_name} &gt;
                </span>
              )}{' '}
              {bookingLine.room_name || bookingLine.room_type_name + ' N/A'}
            </span>

            {[
              BookingStatus.CONFIRM,
              BookingStatus.CHECK_IN,
              BookingStatus.CHECK_OUT,
              BookingStatus.CANCEL
            ].includes(bookingLine.status) && (
              <Dropdown
                menu={{ items: menuAssignRoom, onClick: handleClickMenu }}
                trigger={['click']}
              >
                <Button icon={<EllipsisOutlined style={{ fontSize: '20px' }} />} type="text" />
              </Dropdown>
            )}
          </div>
          <BookingAction bookingLine={bookingLine} t={t} />
        </div>
        <div className="customer-range-date">
          <Button
            disabled={isCancelOrCheckOut}
            type="text"
            className="w-full"
            onClick={handleShowChangeDateModal}
          >
            <div className="flex items-center">
              <CalendarOutlined />
              <div>
                <span className="time">{toLocalTime(bookingLine.check_in, 'DD/MM - HH:mm')}</span>
                <SwapRightOutlined />
                <span className="time">{toLocalTime(bookingLine.check_out, 'DD/MM - HH:mm')}</span>
              </div>
            </div>
          </Button>
        </div>

        <div className="customer-price">
          <Button
            type="text"
            className="w-full"
            onClick={handleShowChangePriceModal}
            disabled={isCancelOrCheckOut}
          >
            <DollarCircleOutlined />
            <span>
              {bookingLine.pricelist?.name || t('common.bookingData.defaultPrice')} (
              {t('common.total')}: {formatCurrency(bookingLine.subtotal_price)} )
            </span>
          </Button>
        </div>
        <div className="customer-type">
          <IBButton
            type="text"
            className="w-full"
            onClick={handleShowCustomerModal}
            disabled={isCancelOrCheckOut}
            permission={PermissionActionTypes.VIEW_CUSTOMER_INFO}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <UserOutlined />
                <span>
                  {bookingLine?.adult} {t('common.customer.adult')}, {bookingLine.child}{' '}
                  {t('common.customer.child')}
                </span>
              </div>
              <PlusCircleOutlined style={{ color: '#9C4F3B' }} />
            </div>
          </IBButton>
        </div>
        <div className="customer-list">
          {bookingLine?.customers?.map((item, index) => (
            <IBButton
              key={item.partner_id}
              type="text"
              className="w-full"
              onClick={() => {
                setSelectedCustomerId(index);
                handleShowCustomerModal();
              }}
              permission={PermissionActionTypes.VIEW_CUSTOMER_INFO}
            >
              <div className="customer-name" key={item.partner_id}>
                <span>{item.name}</span>
                <div className="flex items-center">
                  <IdcardOutlined />
                </div>
              </div>
            </IBButton>
          ))}
        </div>
        {bookingLine.status === BookingStatus.CANCEL && (
          <div className="cancel-note">
            <span className="required">
              {t('common.statuses.cancelled')}: {bookingLine?.cancel_reason}
            </span>
          </div>
        )}
      </Col>

      <Col span={24} md={8}>
        <Form
          form={form}
          name="booking-line-form"
          layout="vertical"
          style={{ width: '100%' }}
          initialValues={{
            note: bookingLine.note || '',
            label_name: bookingLine.labels?.map(label => label.name) || [],
            cms_ota_id: bookingLine.cms_ota_id || ''
          }}
          onFinish={onFinish}
          autoComplete="off"
          disabled={isCancelOrCheckOut}
        >
          <Form.Item name="note">
            <Input.TextArea placeholder={t('common.note')} autoSize={{ minRows: 3, maxRows: 7 }} />
          </Form.Item>
          <Form.Item name="hotel_travel_agency_id">
            <Select
              options={
                travelAgencies &&
                travelAgencies.map(travelAgency => ({
                  value: travelAgency.id,
                  label: travelAgency.name,
                  source_id: travelAgency.source_id,
                  medium_id: travelAgency.medium_id
                }))
              }
              showSearch
              filterOption={(input, option) =>
                !!option && option.label?.toLowerCase().includes(input.toLowerCase())
              }
              onSearch={handleSearchTravelAgency}
              onClear={() => setSelectedTravelAgency({})}
              onChange={(value, record) => handleTravelAgencyChange(value, record)}
              placeholder={t('bookingPage.bookingList.travelAgency')}
              allowClear
            />
          </Form.Item>
          <Form.Item name="medium_id">
            <Select
              options={companies}
              showSearch
              filterOption={(input, option) =>
                !!option && option.name.toLowerCase().includes(input.toLowerCase())
              }
              fieldNames={{ label: 'name', value: 'id' }}
              placeholder={t('managePage.travelAgencyPage.mediumName')}
            />
          </Form.Item>
          <Form.Item name="source_id">
            <Select
              options={sources}
              showSearch
              onChange={handleSourceChange}
              filterOption={(input, option) =>
                !!option && option.name.toLowerCase().includes(input.toLowerCase())
              }
              fieldNames={{ label: 'name', value: 'id' }}
              placeholder={t('managePage.travelAgencyPage.sourceName')}
            />
          </Form.Item>
          <Form.Item name="campaign_id">
            <Select
              options={campaigns}
              showSearch
              fieldNames={{ label: 'name', value: 'id' }}
              filterOption={(input, option) =>
                !!option && option.name.toLowerCase().includes(input.toLowerCase())
              }
              placeholder={t('managePage.travelAgencyPage.campaignName')}
            />
          </Form.Item>

          <Form.Item name="label_name">
            <Select
              options={labels.map((label: Labels) => ({
                value: label.name,
                label: label.name,
                color: label.color
              }))}
              mode="tags"
              placeholder={t('bookingPage.bookingList.labels')}
              onChange={onLabelChange}
              onSearch={handleSearch}
              onClear={() => setSelectedLabel('')}
              onDeselect={value => handleUnassignLabel(value)}
              showSearch
              filterOption={false}
              optionRender={option => (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '5px 10px'
                  }}
                >
                  <span
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: option.data.color || '#f0f0f0',
                      marginRight: '8px'
                    }}
                  />
                  {option.label}
                </span>
              )}
            />
          </Form.Item>
          {showInput && (
            <Form.Item name="cms_ota_id">
              <Input placeholder={t('common.actions.enterOTA')} />
            </Form.Item>
          )}
          {/* <Form.Item name="market" style={{ marginBottom: 0 }}>
            <Input />
          </Form.Item> */}
          {bookingLine.cms_ota_id && (
            <p className="booking-id">
              OTA ID: {bookingLine.cms_ota_id}
              {bookingLine.cms_booking_source && ` (${bookingLine.cms_booking_source})`}{' '}
              {!bookingLine.cms_booking_id && (
                <Button onClick={handleEditOtaId} icon={<EditFilled />}></Button>
              )}
            </p>
          )}

          <div className="room-form__action">
            <Button htmlType="submit" className="ant-btn-secondary" loading={isPending}>
              {t('common.actions.save')}
            </Button>
          </div>
        </Form>
      </Col>

      <Col span={24} md={8}>
        <div className="service">
          <Button
            icon={<IconBreakfast />}
            type="text"
            className="service-breakfast"
            onClick={handleSetBreakfast}
          >
            <span>
              {displayBreakfast
                ? `${t('common.bookingData.hasBreakfast')}: ${displayBreakfast} (${bookingLine.breakfast_dates.length} ${t('common.time.day')})`
                : t('bookingPage.bookingList.nonBreakfast')}
            </span>
          </Button>

          <div className="service-total">
            <span>
              <strong>{t('bookingPage.bookingList.services')} </strong>
              {t('common.total')}: {totalMoney.toLocaleString('vn')}
            </span>
            <Button
              type="link"
              disabled={isCancelOrCheckOut}
              icon={<PlusCircleOutlined />}
              onClick={handleShowAddService}
            ></Button>
          </div>
          <div className="service-wrapper">
            {!!bookingLine.services?.length &&
              bookingLine.services.map(item => {
                return (
                  <div className="service-detail" key={item.id}>
                    <div
                      className={buildClassName({
                        'service-detail__info': true,
                        'line-through': item.is_deleted
                      })}
                    >
                      <span
                        className="service-detail__info-item"
                        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        {item.name} ({item.qty})
                        {item.esim_status != null &&
                          (() => {
                            const { status, total } = item.esim_status;
                            return (
                              <Space size="small" wrap>
                                {status === KinSimStatus.NEW && (
                                  <Button
                                    type="default"
                                    size="small"
                                    onClick={() =>
                                      setIsOpenIssueKinSim(
                                        true,
                                        item.id,
                                        item.campaign_id,
                                        item.qty,
                                        item.guest_id
                                      )
                                    }
                                  >
                                    {t('esim.issue')} ({item.qty})
                                  </Button>
                                )}
                                {status === KinSimStatus.DETAILS && (
                                  <Button
                                    type="default"
                                    size="small"
                                    onClick={() =>
                                      setIsOpenViewKinSimDetails(
                                        true,
                                        item.id,
                                        item.qty,
                                        item.campaign_id
                                      )
                                    }
                                  >
                                    {`${t('esim.viewDetails')} (${total})`}
                                  </Button>
                                )}
                              </Space>
                            );
                          })()}
                      </span>
                      <span className="service-detail__info-price">
                        {formatCurrency(item.subtotal_price)}
                      </span>
                    </div>
                    <div className="service-detail__name">
                      <span>
                        {toLocalTime(item.created_date, 'DD/MM HH:mm')} - {item.username}
                      </span>
                    </div>

                    <Button
                      className="service-detail__remove"
                      type="link"
                      icon={<CloseCircleFilled />}
                      onClick={() => handleRemoveService(item.id)}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default RoomInfoTab;
